export type HealthBarSize = 'hero' | 'row' | 'sub';

export type HealthBarProps = {
  value: number;
  previous?: number;
  size: HealthBarSize;
  thresholds: { warn: number; critical: number };
  projection?: { value: number; label?: string };
};

const SIZE_HEIGHT: Record<HealthBarSize, number> = {
  hero: 16,
  row: 10,
  sub: 6,
};

// Bands fill the full track left-to-right: critical (0..crit), warn
// (crit..warn), watch (warn..warn+10), healthy (rest). The +10 watch
// shoulder matches the design spec's reading of "watch" as the
// transitional band immediately above warn. The screens spec uses these
// tokens; the band widths derive from props.thresholds.
function bands(thresholds: { warn: number; critical: number }) {
  const watchTop = Math.min(100, thresholds.warn + 10);
  return [
    { from: 0, to: thresholds.critical, className: 'fill-critical-bg' },
    { from: thresholds.critical, to: thresholds.warn, className: 'fill-warn-bg' },
    { from: thresholds.warn, to: watchTop, className: 'fill-watch-bg' },
    { from: watchTop, to: 100, className: 'fill-healthy-bg' },
  ];
}

/**
 * Fleet-wide 0–100 health visualization. MUST render identically across
 * all screens — the visual contract is owned here. Uses an inline SVG so
 * a single source can serve hero / row / sub sizes without re-layout.
 */
export function HealthBar({
  value,
  previous,
  size,
  thresholds,
  projection,
}: HealthBarProps) {
  const height = SIZE_HEIGHT[size];
  const clamp = (v: number) => Math.max(0, Math.min(100, v));
  const v = clamp(value);
  const p = previous !== undefined ? clamp(previous) : undefined;
  const proj = projection ? clamp(projection.value) : undefined;

  const label =
    `Health ${Math.round(v)} of 100` +
    (p !== undefined ? `, previously ${Math.round(p)}` : '') +
    (proj !== undefined ? `, projected ${Math.round(proj)}` : '');

  return (
    <div className="flex w-full items-center gap-sp-2" data-size={size}>
      <svg
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        role="meter"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={v}
        aria-label={label}
        className="h-auto w-full"
        style={{ height }}
      >
        <rect x="0" y="0" width="100" height={height} className="fill-gray-100" />
        {bands(thresholds).map((b) => (
          <rect
            key={b.from}
            x={b.from}
            y="0"
            width={Math.max(0, b.to - b.from)}
            height={height}
            className={b.className}
          />
        ))}
        {p !== undefined ? (
          <line
            x1={p}
            x2={p}
            y1="0"
            y2={height}
            strokeDasharray="2 2"
            strokeWidth="1.2"
            className="stroke-gray-500"
          />
        ) : null}
        {proj !== undefined ? (
          <polygon
            points={`${proj - 1.5},0 ${proj + 1.5},0 ${proj},${height / 2}`}
            className="fill-info"
          />
        ) : null}
        <line
          x1={v}
          x2={v}
          y1="0"
          y2={height}
          strokeWidth="2"
          className="stroke-gray-900"
        />
      </svg>
      {size === 'hero' ? (
        <span className="min-w-[3ch] text-right font-mono text-fs-statement text-gray-900">
          {Math.round(v)}
        </span>
      ) : null}
    </div>
  );
}
